import React, { useRef } from "react";
import { useParams, Navigate } from "react-router-dom";
import ProjectDetail from "../../components/sections/partials/ProjectDetail";
import Seo from "../../seo/Seo";
import { absoluteUrl } from "../../seo/defaults";
import { projects } from "../../data/projects";

const stripHtml = (text) => (text ? text.replace(/<[^>]*>/g, " ") : text);

const truncate = (text, n = 160) => {
  if (!text) return null;
  const normalized = text.replace(/\s+/g, " ").trim();
  if (normalized.length <= n) return normalized;
  return `${normalized.slice(0, n - 1).trimEnd()}…`;
};

const ProjectPage = () => {
  const childRef = useRef();
  const { slug } = useParams();
  const project = projects.find((p) => p.id === slug);

  if (!project) {
    return <Navigate to="/page-not-found" replace />;
  }

  const isHtml = project.contentFormat === "html";

  const canonicalPath = `/our-works/${project.id}`;
  const description =
    truncate(isHtml ? stripHtml(project.upperDetail) : project.upperDetail) ||
    truncate(project.lowerDetail) ||
    `${project.name} — case study by Trade Square.`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: project.name,
    headline: project.name,
    description,
    url: absoluteUrl(canonicalPath),
    image: project.image ? absoluteUrl(project.image) : undefined,
    keywords: Array.isArray(project.tags) ? project.tags.join(", ") : undefined,
    author: {
      "@type": "Organization",
      name: "Trade Square Co., Ltd.",
      url: absoluteUrl("/"),
    },
  };

  const contentMessage = isHtml ? (
    // First-party, staff-authored CMS content (the OAuth proxy requires repo
    // push access to edit), so rendering it as HTML is acceptable here.
    <div
      className="project-richtext"
      dangerouslySetInnerHTML={{
        __html: [project.upperDetail, project.lowerDetail]
          .filter(Boolean)
          .join("\n"),
      }}
    />
  ) : (
    <div className="text-xs my-0">
      {project.upperDetail && (
        <p className="m-0 mb-12">{project.upperDetail}</p>
      )}
      {project.lowerDetail && (
        <p className="m-0 mb-12">{project.lowerDetail}</p>
      )}
      {project.projectOwner && (
        <p className="m-0">- {project.projectOwner}</p>
      )}
    </div>
  );

  return (
    <>
      <Seo
        title={`${project.name} — Case Study`}
        description={description}
        canonical={canonicalPath}
        ogImage={project.image || undefined}
        type="article"
        jsonLd={jsonLd}
      />
      <ProjectDetail
        childRef={childRef}
        projectName={project.name}
        projectLogoImg={project.logoImage}
        tagNames={project.tags}
        topImg={project.image}
        contentMessage={contentMessage}
        messageBlock1={null}
        messageBlock2={null}
        block1Img={null}
        block2Img={null}
        previousPageText="Our Work"
        previousPageLink="/our-works"
      />
    </>
  );
};

export default ProjectPage;
